import { Box, Button, Divider, Typography, useMediaQuery } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useEffect, useState } from 'react';
import Filter from './Filter';

// Updated FilterCategory interface for string-based filters
interface FilterCategory {
  title: string;
  available: string[];         // Now all string arrays
  filtered: string[];          // Now all string arrays
  list: string[];
  setList: React.Dispatch<React.SetStateAction<string[]>>;
  keyName: string;
  paramKey: string;
}

interface FilterSelectionProps {
  // Available options (all string arrays now)
  availableColours: string[];
  availableDesigns: string[];
  availableTypes: string[];
  availableBrands: string[];
  availableMaterials: string[];
  availableStandards: string[];
  availableSizes: string[];
  availableShapes: string[];

  // Filtered options (all string arrays now)
  filteredColours: string[];
  filteredDesigns: string[];
  filteredTypes: string[];
  filteredBrands: string[];
  filteredMaterials: string[];
  filteredStandards: string[];
  filteredSizes: string[];
  filteredShapes: string[];

  // Currently-selected values
  colourList: string[];
  setColourList: React.Dispatch<React.SetStateAction<string[]>>;
  designList: string[];
  setDesignList: React.Dispatch<React.SetStateAction<string[]>>;
  typesList: string[];
  setTypesList: React.Dispatch<React.SetStateAction<string[]>>;
  brandList: string[];
  setBrandList: React.Dispatch<React.SetStateAction<string[]>>;
  materialList: string[];
  setMaterialList: React.Dispatch<React.SetStateAction<string[]>>;
  standardsList: string[];
  setStandardsList: React.Dispatch<React.SetStateAction<string[]>>;
  sizeList: string[];
  setSizeList: React.Dispatch<React.SetStateAction<string[]>>;
  shapeList: string[];
  setShapeList: React.Dispatch<React.SetStateAction<string[]>>;

  // Handlers & other props
  handleCheckboxChange: (
    elem: string,
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    list: string[],
    paramKey: string
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  applyFilters: () => void;
  applicationsList: string[];
  anchorRef: React.RefObject<HTMLDivElement>;
  itemGroup: string;
}

const FilterSelection = ({
                           // Available options
                           availableColours,
                           availableDesigns,
                           availableTypes,
                           availableBrands,
                           availableMaterials,
                           availableStandards,
                           availableSizes,
                           availableShapes,

                           // Filtered options
                           filteredColours,
                           filteredDesigns,
                           filteredTypes,
                           filteredBrands,
                           filteredMaterials,
                           filteredStandards,
                           filteredSizes,
                           filteredShapes,

                           // Selected values
                           colourList,
                           setColourList,
                           designList,
                           setDesignList,
                           typesList,
                           setTypesList,
                           brandList,
                           setBrandList,
                           materialList,
                           setMaterialList,
                           standardsList,
                           setStandardsList,
                           sizeList,
                           setSizeList,
                           shapeList,
                           setShapeList,

                           // Handlers
                           handleCheckboxChange,
                           applyFilters,
                           applicationsList,
                           itemGroup,
                         }: FilterSelectionProps) => {
  const isMobile = useMediaQuery('(max-width:600px)');

  const [visibility, setVisibility] = useState<{ [key: string]: boolean }>({
    Colour: false,
    Design: false,
    Types: false,
    Brand: false,
    Material: false,
    Standards: false,
    Size: false,
    Shape: false,
  });

  useEffect(() => {
    // If on mobile, collapse all sections by default
    if (isMobile) {
      setVisibility({
        Colour: false,
        Design: false,
        Types: false,
        Brand: false,
        Material: false,
        Standards: false,
        Size: false,
        Shape: false,
      });
    }
  }, [isMobile]);

  /**
   * A helper that returns filtered options. If no application is selected,
   * we return all available items. With the new structure, all filtering
   * is done based on string values directly.
   */
  const getFilteredOptions = (available: string[], filtered: string[], category: string): string[] => {
    // If no application selected, return entire array
    if (!applicationsList || applicationsList.length === 0) {
      // For Types, filter out "-" even when no applications are selected
      if (category === 'Types') {
        return available.filter(item => item !== '-');
      }
      return available;
    }

    // If filtered is empty or undefined, still show all available options
    if (!filtered || filtered.length === 0) {
      // For Types, filter out "-" even when no filtered options
      if (category === 'Types') {
        return available.filter(item => item !== '-');
      }
      return available;
    }

    // With the new API structure, filtered options are already strings
    return filtered.filter(item => item !== '-');
  };

  // Define filter categories with the updated structure
  const filterCategories: FilterCategory[] = [
    {
      title: 'Colour',
      available: availableColours,
      filtered: filteredColours,
      list: colourList,
      setList: setColourList,
      keyName: 'web_colour',
      paramKey: 'colours',
    },
    {
      title: 'Design',
      available: availableDesigns,
      filtered: filteredDesigns,
      list: designList,
      setList: setDesignList,
      keyName: 'design_style',
      paramKey: 'design_style',
    },
    {
      title: 'Types',
      available: availableTypes,
      filtered: filteredTypes,
      list: typesList,
      setList: setTypesList,
      keyName: 'web_type',
      paramKey: 'types',
    },
    {
      title: 'Brand',
      available: availableBrands,
      filtered: filteredBrands,
      list: brandList,
      setList: setBrandList,
      keyName: 'brand',
      paramKey: 'brand',
    },
    {
      title: 'Material',
      available: availableMaterials,
      filtered: filteredMaterials,
      list: materialList,
      setList: setMaterialList,
      keyName: 'web_materials',
      paramKey: 'material',
    },
    {
      title: 'Standards',
      available: availableStandards,
      filtered: filteredStandards,
      list: standardsList,
      setList: setStandardsList,
      keyName: 'web_standards',
      paramKey: 'standards',
    },
    {
      title: 'Size',
      available: availableSizes,
      filtered: filteredSizes,
      list: sizeList,
      setList: setSizeList,
      keyName: 'web_size',
      paramKey: 'sizes',
    },
    // Add shape category for the new API structure
    {
      title: 'Shape',
      available: availableShapes,
      filtered: filteredShapes,
      list: shapeList,
      setList: setShapeList,
      keyName: 'web_shape',
      paramKey: 'shapes',
    },
  ];

  return (
    <>
      {filterCategories.map((category) => {
        const filteredAvailable = getFilteredOptions(
          category.available,
          category.filtered,
          category.title
        );

        // If after filtering we have nothing, skip rendering
        if (!filteredAvailable || filteredAvailable.length === 0) {
          return null;
        }

        return (
          <Box key={category.title}>
            <Divider sx={{ backgroundColor: '#000000', m: '1rem 0' }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography fontWeight="bold" fontSize="20px" mb={2}>
                {category.title}
              </Typography>
              <Box
                onClick={() =>
                  setVisibility((prev) => ({ ...prev, [category.title]: !prev[category.title] }))
                }
                sx={{ cursor: 'pointer' }}
              >
                {visibility[category.title] ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
              </Box>
            </Box>
            {visibility[category.title] && (
              <Filter
                filterType={filteredAvailable}
                handleCheckboxChange={handleCheckboxChange}
                list={category.list}
                setList={category.setList}
                keyName={category.keyName}
                paramKey={category.paramKey}
              />
            )}
          </Box>
        );
      })}
      <Divider sx={{ backgroundColor: '#000000', m: '1rem 0' }} />
      <Button
        variant="contained"
        onClick={applyFilters}
        sx={{ width: { xs: '100%', lg: '50%' } }}
        disableElevation
      >
        Apply
      </Button>
      <Divider sx={{ backgroundColor: '#000000', m: '1rem 0' }} />
    </>
  );
};

export default FilterSelection;